import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import AtomLoader from '../components/AtomLoader';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try {
      const user = await login(username, password);
      toast.success(`Welcome back, ${user.name}!`);
      const routes = { super_admin: '/admin', school_lab: '/lab', school_authority: '/school', weguide_authority: '/weguide' };
      navigate(routes[user.role] || '/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split" data-testid="login-page">
      {loading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/70 backdrop-blur-md animate-fade-in">
          <AtomLoader text="Authenticating Securely..." />
        </div>
      )}
      {/* Left: Video */}
      <div className="hidden md:block relative overflow-hidden bg-[#0D6EDE]">
        <video
          className="absolute inset-0 h-full w-full object-cover object-center"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src="/logo-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-700/25 to-cyan-400/20" />
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-4 sm:p-6 md:p-12 bg-white relative glow-bg overflow-hidden">
        <div className="absolute top-4 right-4 md:top-8 md:right-8 z-10 animate-fade-in">
          <div className="flex items-center gap-3 rounded-full border border-slate-200/80 bg-white/80 px-4 py-2 shadow-lg shadow-slate-200/60 backdrop-blur-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 shadow-md shadow-cyan-200/70">
              <img src="/logo.png" alt="We Guide Logo" className="h-6 w-6 object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>We Guide</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400">Dashboard</div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg animate-slide-right pt-16 md:pt-14">
          
          <div className="glass-panel rounded-2xl p-5 sm:p-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Sign in</h2>
            <p className="text-sm text-slate-500 mb-6">Enter your credentials to access your dashboard</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-xs font-semibold text-slate-600 mb-1.5 block">Username</Label>
                <Input
                  id="username"
                  data-testid="login-username"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-white/50 border-slate-200 focus:border-blue-400 focus:ring-4 focus:ring-blue-100/50 rounded-xl h-12"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs font-semibold text-slate-600 mb-1.5 block">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white/50 border-slate-200 focus:border-blue-400 focus:ring-4 focus:ring-blue-100/50 rounded-xl h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password"
                  >
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full gradient-btn rounded-full h-12 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ transition: 'box-shadow 0.2s, transform 0.15s' }}
              >
                {loading ? (
                  <>Authenticating...</>
                ) : (
                  <>Sign in <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Powered by We Guide Education Platform
          </p>
        </div>
      </div>
    </div>
  );
}
